import React, { useContext, useEffect, useMemo, useState } from 'react';
import RowFilterEditor from './RowFilterEditor';
import { useHistory, useParams } from 'react-router-dom';
import AppContext from 'Utils/AppContext';
import { getSourceNomenclature } from 'Utils/nomenclature';
import { getDataSourceById, toUiDataSource } from 'DataSources/datasourcesApi';
import SalesforceObjectDetail from 'Metadata/SalesforceNetwork/ObjectDetail';
import Breadcrumbs from 'Utils/Breadcrumbs';
import { apiErrorText } from 'Utils/errorText';
import { getDataGraph, updateDataGraph, graphUpdateBody, graphOutcome, withObject, withoutObject } from './dataGraphApi';
import { GRAPH_BASE, sourcePath, graphPath, objectPath } from './paths';

/**
 * One Salesforce object within a data graph (…/:dataSourceId/graphs/:graphId/object/:objectName).
 * The body is the profile page's object page, drawn through this graph (its manual links); for a LIST
 * graph it also carries the graph's own object list — the same bar as the list, with Add / Remove for
 * this object, saved at once (this page has no draft of the graph's settings; 2026-11-04).
 */
const GraphObjectPage = () => {
  const { dataSourceId, graphId, objectName: objectParam } = useParams();
  const objectName = decodeURIComponent(objectParam);
  const history = useHistory();
  const { connections, isReadOnly } = useContext(AppContext);
  const connection = useMemo(() => (connections || []).find((c) => String(c.id) === String(dataSourceId)), [connections, dataSourceId]);
  const [source, setSource] = useState(null);
  const [graph, setGraph] = useState(null);

  useEffect(() => {
    let cancelled = false;
    getDataGraph(graphId).then((g) => { if (!cancelled) setGraph(g); }).catch(() => {});
    return () => { cancelled = true; };
  }, [graphId]);

  useEffect(() => {
    let cancelled = false;
    getDataSourceById(dataSourceId)
      .then((dto) => { if (!cancelled && dto) setSource(toUiDataSource(dto)); })
      .catch(() => {});
    return () => { cancelled = true; };
  }, [dataSourceId]);

  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState(null);
  const listed = useMemo(() => new Set(((graph && graph.objects) || []).map((o) => o.name)), [graph]);
  const toggle = async (table) => {
    if (!graph) return;
    setSaving(true);
    setSaveError(null);
    try {
      const objects = listed.has(table) ? withoutObject(graph.objects, table) : withObject(graph.objects, table);
      setGraph(await updateDataGraph(graph.id, graphUpdateBody(graph, graph.tableFilters || [], objects)));
    } catch (e) {
      setSaveError(apiErrorText(e, 'Could not change the data graph'));
    } finally {
      setSaving(false);
    }
  };
  const choice = graph && graphOutcome(graph) === 'LIST' && !isReadOnly ? {
    kind: 'graph', immediate: true, picked: listed, saved: listed, dirty: false, saving, saveError, toggle,
    pickAll: () => {}, save: () => {}, revert: () => {}, clear: () => {},
  } : null;
  const name = (source && source.name) || (connection && connection.name) || `Source ${dataSourceId}`;

  return (
    <div className='dg-page'>
      <div className='dg-breadcrumb d-flex align-items-center flex-wrap'>
        <Breadcrumbs className='mb-0' items={[
          { label: 'Data Graph', to: GRAPH_BASE },
          { label: name, to: sourcePath(dataSourceId) },
          { label: (graph && graph.name) || `Data graph ${graphId}`, to: graphPath(dataSourceId, graphId), testId: 'dg-object-back' },
          { label: objectName },
        ]} />
        <span> · </span>
        <button type='button' className='dg-link' data-testid='dg-object-profile-view'
          onClick={() => history.push(`/metadata/source/${encodeURIComponent(dataSourceId)}/object/${encodeURIComponent(objectName)}`)}>
          Fingerprint view
        </button>
      </div>
      <SalesforceObjectDetail
        dataSourceId={dataSourceId}
        graphId={graphId}
        objectName={objectName}
        nouns={getSourceNomenclature('salesforce')}
        choice={choice}
        onOpenObject={(table) => history.push(objectPath(dataSourceId, graphId, table))}
        filterSlot={(node) => graph && (
          <RowFilterEditor graph={graph} table={objectName} engine='salesforce'
            recordTypes={node && node.attributes ? node.attributes.recordTypes : null}
            canEdit={!isReadOnly} onSaved={setGraph} />
        )}
      />
    </div>
  );
};

export default GraphObjectPage;
